import React, { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import {
  ArrowRight,
  BadgeIndianRupee,
  Headphones,
  MapPin,
  ShieldCheck,
  ShoppingBag,
  Zap,
} from "lucide-react";
import API_URL from "../config/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const leftPanelHighlights = [
  {
    icon: Zap,
    title: "Fast Delivery",
    description: "Quick and reliable delivery at your doorstep",
  },
  {
    icon: ShieldCheck,
    title: "Safe & Secure",
    description: "Your parcels are in safe hands",
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    description: "We're here to help you anytime",
  },
];

const bottomFeatures = [
  {
    icon: ShoppingBag,
    title: "Deliver Anything",
    description: "Anything, anywhere, anytime",
  },
  {
    icon: BadgeIndianRupee,
    title: "Affordable Pricing",
    description: "Best rates for quick deliveries",
  },
  {
    icon: MapPin,
    title: "Live Tracking",
    description: "Track your order in real-time",
  },
  {
    icon: ShieldCheck,
    title: "Trusted Service",
    description: "Thousands of happy customers",
  },
];

const Signin = ({
  showSigninModal,
  setShowSigninModal,
  setShowSignupModal,
  setShowOTPModal,
  setMobileForOTP,
  setOtpIntent,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!showSigninModal) {
      return undefined;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [showSigninModal]);

  const mobileno = watch("mobileno", "");
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length > 10) return mobileno;
    if (digits.length > 5) {
      return `${digits.slice(0, 5)} ${digits.slice(5, 10)}`;
    }
    return digits;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue("mobileno", formatted, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const mobile = data.mobileno.replace(/\D/g, "");
      const response = await axios.post(`${API_URL}/otp/send-otp`, {
        mobile, accountType: "customer", purpose: "customer-auth", authMode: "login",
      });
      if (!response.data.success) throw new Error(response.data.message || "Unable to send OTP");
      setOtpIntent({ authMode: "login" });
      setMobileForOTP(mobile);
      setShowSigninModal(false);
      setShowOTPModal(true);
      toast.success("OTP sent to your mobile number");
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Unable to send OTP");
    } finally {
      setLoading(false);
    }
  };

  if (!showSigninModal) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/55 p-2 sm:p-3 lg:p-4">
      <div className="mx-auto flex min-h-full items-center justify-center">
        <div className="relative w-full max-w-[1040px] max-h-[calc(100dvh-0.75rem)] rounded-[22px] bg-[#fffdf7] p-1.5 shadow-[0_24px_90px_rgba(0,0,0,0.28)] sm:max-h-[calc(100dvh-1rem)] sm:rounded-[28px] sm:p-2 lg:max-h-[calc(100dvh-1.25rem)]">
        <button
          onClick={() => setShowSigninModal(false)}
          className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[28px] leading-none text-slate-400 shadow-[0_8px_20px_rgba(15,23,42,0.10)] transition hover:text-slate-600 sm:right-4 sm:top-4"
          aria-label="Close sign in form"
        >
          &times;
        </button>

          <div className="flex h-full max-h-[calc(100dvh-0.75rem)] flex-col overflow-hidden rounded-[20px] border border-[#efe8d1] bg-white shadow-[0_16px_45px_rgba(167,139,84,0.10)] sm:max-h-[calc(100dvh-1rem)] sm:rounded-[26px] lg:max-h-[calc(100dvh-1.25rem)]">
            <div className="grid min-h-0 flex-1 lg:grid-cols-[34%_66%]">
              <div className="relative hidden min-h-0 overflow-hidden rounded-l-[26px] rounded-tr-none lg:block">
              <img
                src="/assets/delivery_man_images/sign_in.png"
                alt="Delivery partner"
                className="h-full w-full object-cover object-[center_12%] xl:object-center"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#14210d]/8 via-transparent to-transparent" />

              <div className="absolute bottom-3 left-3 right-3 max-w-[250px] rounded-[18px] border border-white/10 bg-[#181818]/82 p-3 text-white shadow-[0_18px_40px_rgba(0,0,0,0.32)] backdrop-blur-[6px] sm:left-4 sm:right-auto sm:p-4">
                <div className="space-y-3">
                  {leftPanelHighlights.map(({ icon: Icon, title, description }) => (
                    <div key={title} className="flex items-start gap-2.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f3c400] text-[#fff8d7] shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]">
                        <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold leading-4">{title}</p>
                        <p className="mt-0.5 text-[12px] leading-4 text-white/86">
                          {description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

              <div className="overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(255,255,255,1),_rgba(255,252,244,0.96)_60%,_rgba(255,248,228,0.9)_100%)] px-4 pb-4 pt-8 sm:px-5 sm:pt-9 lg:px-8 lg:pb-5 lg:pt-8">
                <div className="mx-auto flex w-full max-w-[430px] flex-1 flex-col">
                  <div className="mb-2 flex justify-center lg:mb-3">
                  <img
                    src="/assets/logo/logo.png"
                    alt="Instaport Delivery"
                    className="h-auto w-[140px] max-w-full sm:w-[170px] lg:w-[190px]"
                  />
                </div>

                  <div className="text-left">
                  <h2 className="text-[24px] font-extrabold tracking-[-0.02em] text-[#0a0a14] sm:text-[30px] lg:text-[34px]">
                    Welcome Back!
                  </h2>
                  <p className="mt-1.5 text-[13px] leading-5 text-[#7b8398] sm:text-[14px] lg:text-[15px]">
                    Sign in to continue and manage your deliveries
                  </p>
                </div>

                <form
                    className="mt-4 space-y-3 lg:mt-4 lg:space-y-3.5"
                  onSubmit={(e) => handleSubmit(onSubmit)(e)}
                >
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-[#131313] sm:text-[14px]">
                      Phone Number
                    </label>
                    <div className="flex overflow-hidden rounded-[14px] border border-[#d7dbe6] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
                      <span className="flex min-w-[64px] items-center justify-center border-r border-[#d7dbe6] bg-[#fff6df] px-3 text-[15px] font-medium text-[#191919] sm:min-w-[76px] sm:text-[16px]">
                        +91
                      </span>
                      <input
                        type="text"
                        {...register("mobileno", {
                          required: "Phone number is required",
                          pattern: {
                            value: /^\d{5}\s\d{5}$/,
                            message: "Invalid phone number format (e.g., 66666 66667)",
                          },
                        })}
                        onChange={handlePhoneChange}
                        placeholder="999999999"
                        maxLength={11}
                        className="w-full bg-white px-4 py-2.5 text-[14px] text-[#1e2430] outline-none placeholder:text-[#8f96aa] sm:px-4 sm:py-3 sm:text-[15px]"
                      />
                    </div>
                    {errors.mobileno && (
                      <p className="mt-2 text-sm text-red-500">
                        {errors.mobileno.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2.5 rounded-[14px] bg-gradient-to-r from-[#ff9900] to-[#ffb000] px-5 py-2.5 text-[15px] font-medium text-white shadow-[0_16px_30px_rgba(255,170,0,0.28)] transition hover:from-[#f39200] hover:to-[#ffab00] disabled:cursor-not-allowed disabled:opacity-70 sm:py-3 sm:text-[17px]"
                  >
                    <ArrowRight className="h-4.5 w-4.5" strokeWidth={2.2} />
                    {loading ? "Sending OTP..." : "Send OTP"}
                  </button>
                </form>

                <div className="mt-3 text-center text-[13px] text-[#202020] sm:mt-3.5 sm:text-[14px]">
                  <span>Don&apos;t have an account? </span>
                  <button
                    onClick={() => {
                      setShowSigninModal(false);
                      setShowSignupModal(true);
                    }}
                    className="font-medium text-[#ff9f0a] transition hover:text-[#f08c00]"
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            </div>
          </div>

            <div className="border-t border-[#efe8d1] bg-[linear-gradient(180deg,_#fffefb_0%,_#fffaf0_100%)] px-4 py-2 sm:px-5 sm:py-2.5">
              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-3">
              {bottomFeatures.map(({ icon: Icon, title, description }) => (
                <div key={title} className="flex items-start gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#fff1be] text-[#f2a000] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                    <Icon className="h-4 w-4" strokeWidth={2.1} />
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold leading-4 text-[#171717] sm:text-[13px]">
                      {title}
                    </p>
                    <p className="mt-0.5 max-w-[150px] text-[11px] leading-4 text-[#596173] sm:text-[12px]">
                      {description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Signin;
